import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { User, Phone, GraduationCap, Calendar, Flag, Users, ChevronRight, Shield, QrCode, CheckCircle } from 'lucide-react'
import useStore from '../store'
import { calculateMatchScore } from '../utils/matchEngine'

const SCHOOL_OPTIONS = ['985', '211', '双一流', '一本', '二本', '专科']
const POLITICAL_OPTIONS = ['党员', '预备党员', '团员', '群众']
const IDENTITY_OPTIONS = ['应届生', '择业期应届生', '往届生']
const GENDER_OPTIONS = ['男', '女']

const InputField = ({ icon: Icon, label, name, type = 'text', value, onChange, required = true, options, placeholder }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-gray-700 mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="relative">
      {Icon && (
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
          <Icon size={18} />
        </div>
      )}
      {options ? (
        <select
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          className={`input-field ${Icon ? 'pl-10' : ''}`}
        >
          <option value="">请选择{label}</option>
          {options.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          placeholder={placeholder}
          className={`input-field ${Icon ? 'pl-10' : ''}`}
        />
      )}
    </div>
  </div>
)

const BasicMatchPage = () => {
  const navigate = useNavigate()
  const { setAssessmentData, setMatchResult, setPrivacyAgreed, privacyAgreed } = useStore()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showPrivacy, setShowPrivacy] = useState(false)
  const [errors, setErrors] = useState({})
  
  // Step 1: QR code page, Step 2: Form
  const [currentStep, setCurrentStep] = useState(1)
  
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    gender: '',
    school: '',
    major: '',
    politicalStatus: '',
    identity: '',
    phone: ''
  })

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const validateForm = () => {
    const newErrors = {}
    
    if (!formData.name.trim()) newErrors.name = '请输入姓名'
    if (!formData.age || formData.age < 16 || formData.age > 45) newErrors.age = '请输入有效年龄（16-45岁）'
    if (!formData.gender) newErrors.gender = '请选择性别'
    if (!formData.school) newErrors.school = '请选择学校层次'
    if (!formData.major.trim()) newErrors.major = '请输入专业'
    if (!formData.politicalStatus) newErrors.politicalStatus = '请选择政治面貌'
    if (!formData.identity) newErrors.identity = '请选择身份'
    
    const phoneRegex = /^1[3-9]\d{9}$/
    if (!phoneRegex.test(formData.phone)) newErrors.phone = '请输入有效的11位手机号'
    
    if (!privacyAgreed) newErrors.privacy = '请同意隐私协议'
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) return
    
    setIsSubmitting(true)
    
    try {
      const result = calculateMatchScore(formData)
      setAssessmentData(formData)
      setMatchResult(result)
      navigate('/match-result')
    } catch (error) {
      console.error('提交失败:', error)
      setErrors({ submit: '提交失败，请稍后重试' })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Step 1: QR Code Page
  if (currentStep === 1) {
    return (
      <div className="max-w-md mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="section-title">关注公众号开始测评</h1>
          <p className="section-subtitle">
            扫码关注微信公众号，解锁完整测评功能
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card p-8 text-center"
        >
          {/* QR Code Area */}
          <div className="bg-white p-6 rounded-xl border-2 border-primary-200 mb-6">
            <div className="flex items-center justify-center mb-4">
              <QrCode className="text-primary-600 mr-2" size={24} />
              <span className="font-medium text-gray-800 text-lg">微信扫码关注</span>
            </div>
            
            <div className="flex justify-center mb-4">
              <div className="w-56 h-56 bg-gray-100 rounded-lg flex items-center justify-center">
                <img 
                  src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://www.grad-job.com"
                  alt="关注公众号"
                  className="w-52 h-52"
                  onError={(e) => {
                    e.target.style.display = 'none'
                    e.target.nextSibling.style.display = 'flex'
                  }}
                />
                <div className="hidden w-52 h-52 bg-gray-200 rounded-lg items-center justify-center text-gray-500">
                  <QrCode size={48} />
                  <span className="mt-2 text-sm">二维码加载中...</span>
                </div>
              </div>
            </div>
            
            <p className="text-sm text-gray-600 mb-2">
              使用微信扫一扫，关注"央国企求职助手"
            </p>
            <p className="text-xs text-gray-400">
              关注后即可免费进行求职匹配测评
            </p>
          </div>

          {/* Benefits */}
          <div className="space-y-3 mb-6 text-left">
            <div className="flex items-center text-sm text-gray-600">
              <CheckCircle size={16} className="text-green-500 mr-2 flex-shrink-0" />
              <span>获取个人求职匹配度分析报告</span>
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <CheckCircle size={16} className="text-green-500 mr-2 flex-shrink-0" />
              <span>每日央国企招聘信息推送</span>
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <CheckCircle size={16} className="text-green-500 mr-2 flex-shrink-0" />
              <span>专业导师1对1求职指导</span>
            </div>
          </div>

          {/* Continue button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setCurrentStep(2)}
            className="w-full btn-primary text-lg py-4"
          >
            已关注，开始测评
          </motion.button>
        </motion.div>
      </div>
    )
  }

  // Step 2: Assessment Form
  return (
    <div className="max-w-2xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <h1 className="section-title">硬条件初次匹配</h1>
        <p className="section-subtitle">
          填写基本信息，快速了解你的央国企求职匹配度
        </p>
      </motion.div>

      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        onSubmit={handleSubmit}
        className="card p-6 md:p-8"
      >
        {/* 基本信息 */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <User size={20} className="mr-2 text-primary-600" />
            基本信息
          </h3>
          
          <div className="grid md:grid-cols-2 gap-4">
            <InputField
              icon={User}
              label="姓名"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="请输入真实姓名"
              required
            />
            
            <InputField
              icon={Calendar}
              label="年龄"
              name="age"
              type="number"
              value={formData.age}
              onChange={handleChange}
              placeholder="请输入年龄"
              required
            />
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <InputField
              icon={Users}
              label="性别"
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              options={GENDER_OPTIONS}
              required
            />
            
            <InputField
              icon={GraduationCap}
              label="学校层次"
              name="school"
              value={formData.school}
              onChange={handleChange}
              options={SCHOOL_OPTIONS}
              required
            />
          </div>
          
          <InputField
            icon={GraduationCap}
            label="专业"
            name="major"
            value={formData.major}
            onChange={handleChange}
            placeholder="请输入专业名称，如：计算机科学与技术"
            required
          />
          
          <div className="grid md:grid-cols-2 gap-4">
            <InputField
              icon={Flag}
              label="政治面貌"
              name="politicalStatus"
              value={formData.politicalStatus}
              onChange={handleChange}
              options={POLITICAL_OPTIONS}
              required
            />
            
            <InputField
              icon={Users}
              label="身份"
              name="identity"
              value={formData.identity}
              onChange={handleChange}
              options={IDENTITY_OPTIONS}
              required
            />
          </div>
        </div>

        {/* 联系方式 */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Phone size={20} className="mr-2 text-primary-600" />
            联系方式（用于接收报告）
          </h3>
          
          <InputField
            icon={Phone}
            label="手机号"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="请输入11位手机号"
            required
          />
        </div>

        {/* 隐私协议 */}
        <div className="mb-6">
          <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              id="privacy"
              checked={privacyAgreed}
              onChange={(e) => setPrivacyAgreed(e.target.checked)}
              className="mt-1 w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
            />
            <div className="flex-1">
              <label htmlFor="privacy" className="text-sm text-gray-600 cursor-pointer">
                我已阅读并同意
                <button
                  type="button"
                  onClick={() => setShowPrivacy(true)}
                  className="text-primary-600 hover:underline font-medium"
                >
                  《隐私政策》
                </button>
                和
                <button
                  type="button"
                  onClick={() => setShowPrivacy(true)}
                  className="text-primary-600 hover:underline font-medium"
                >
                  《用户协议》
                </button>
                ，授权系统收集并使用我的个人信息进行求职匹配分析。
                <span className="text-red-500">涉及家庭背景等敏感信息将加密存储。</span>
              </label>
            </div>
          </div>
          {errors.privacy && (
            <p className="text-red-500 text-sm mt-2">{errors.privacy}</p>
          )}
        </div>

        {/* 提交按钮 */}
        <motion.button
          type="submit"
          disabled={isSubmitting}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full btn-primary text-lg py-4 flex items-center justify-center"
        >
          {isSubmitting ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              分析中...
            </span>
          ) : (
            <span className="flex items-center">
              查看匹配结果
              <ChevronRight size={20} className="ml-2" />
            </span>
          )}
        </motion.button>

        {errors.submit && (
          <p className="text-red-500 text-center mt-4">{errors.submit}</p>
        )}

        {/* 安全提示 */}
        <div className="mt-4 flex items-center justify-center text-xs text-gray-400">
          <Shield size={14} className="mr-1" />
          <span>数据采用AES-256加密存储，仅用于求职匹配分析</span>
        </div>
      </motion.form>

      {/* 隐私协议弹窗 */}
      {showPrivacy && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">隐私政策</h2>
              <button onClick={() => setShowPrivacy(false)} className="text-gray-400 hover:text-gray-600">
                ✕
              </button>
            </div>
            <div className="text-sm text-gray-600 space-y-3">
              <p>1. 我们收集您的姓名、联系方式、教育背景等信息，仅用于提供求职匹配服务。</p>
              <p>2. 涉及家庭背景等敏感信息采用AES-256加密存储，未经授权不向第三方披露。</p>
              <p>3. 匹配结果仅供参考，不作为录取保证。</p>
              <p>4. 您有权随时查看、修改或删除您的个人信息。</p>
            </div>
            <button
              onClick={() => setShowPrivacy(false)}
              className="mt-6 w-full btn-primary"
            >
              我知道了
            </button>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default BasicMatchPage
